const SFW_HOSTS = new Set([
  'kissandra.ai',
  'www.kissandra.ai',
  'kissandraai.com',
  'www.kissandraai.com',
  'sfw.localhost',
]);
const FULL_HOSTS = new Set(['kissandra.red', 'www.kissandra.red', 'localhost', '127.0.0.1']);
const PRODUCT_HOSTS = new Set([
  'kissandra.ai',
  'www.kissandra.ai',
  'kissandraai.com',
  'www.kissandraai.com',
  'kissandra.red',
  'www.kissandra.red',
  'localhost',
  '127.0.0.1',
  'sfw.localhost',
]);
const ALIAS_HOSTS = new Set([
  'kissandraai.com',
  'www.kissandraai.com',
]);

const NSFW_MEDIA_RE = /_n\.(webp|mp4|webm|gif)$/i;
const UNFILTERED_CATALOG_RE = /^data\/(?:characters\/characters_rankings(?:_modal)?|scenarios\/scenarios-[a-z0-9-]+|outfits\/outfits-[a-z0-9-]+|loc\/[a-z0-9-]+)\.json$/i;

export const SFW_ORIGIN = 'https://kissandra.ai';
export const FULL_ORIGIN = 'https://kissandra.red';
export const SFW_DEV_PORT = '8001';

const urlFromLoose = (value) => {
  if (!value || typeof value !== 'string') return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  try {
    return new URL(trimmed.includes('://') ? trimmed : `http://${trimmed}`);
  } catch {
    return null;
  }
};

export const isSfwDevOrigin = (value) => {
  const url = urlFromLoose(value);
  if (!url) return false;
  const host = url.hostname.toLowerCase();
  return (host === 'localhost' || host === '127.0.0.1') && url.port === SFW_DEV_PORT;
};

const isDevWorkerHost = (value) => {
  const url = urlFromLoose(value);
  if (!url) return false;
  const host = url.hostname.toLowerCase();
  const port = Number(url.port);
  return (host === 'localhost' || host === '127.0.0.1') && port >= 8787 && port <= 8799;
};

export const hostnameOf = (value) => {
  if (!value || typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  try {
    if (trimmed.includes('://')) return new URL(trimmed).hostname.toLowerCase();
  } catch {
    return '';
  }
  return trimmed.split('/')[0].split(':')[0].toLowerCase();
};

const isLocalDevHost = (host) => host.endsWith('.local');

export const sitePolicyFromHost = (hostname) => {
  const host = hostnameOf(hostname);
  if (SFW_HOSTS.has(host)) return 'sfw';
  if (FULL_HOSTS.has(host) || isLocalDevHost(host)) return 'full';
  return 'sfw';
};

const isApiHostname = (host) =>
  (host.endsWith('.kissandra.ai') && host !== 'www.kissandra.ai')
  || (host.endsWith('.kissandra.red') && host !== 'www.kissandra.red');

export const sitePolicyFromOrigin = (origin) => {
  if (isSfwDevOrigin(origin)) return 'sfw';
  const host = hostnameOf(origin);
  if (!host) return 'sfw';
  return sitePolicyFromHost(host);
};

export const sitePolicyFromRequest = (request) => {
  const hostHeader = request?.headers?.get('Host') || '';
  const originHeader = request?.headers?.get('Origin') || '';
  const refererHeader = request?.headers?.get('Referer') || '';
  if (isSfwDevOrigin(hostHeader) || isSfwDevOrigin(originHeader) || isSfwDevOrigin(refererHeader)) return 'sfw';
  const host = hostnameOf(hostHeader);
  if (host && !isApiHostname(host) && !isDevWorkerHost(hostHeader)) return sitePolicyFromHost(host);
  const originHost = hostnameOf(originHeader);
  if (originHost) return sitePolicyFromHost(originHost);
  const refererHost = hostnameOf(refererHeader);
  if (refererHost) return sitePolicyFromHost(refererHost);
  return 'sfw';
};

export const isSfwStorefrontHost = (value) => {
  const host = hostnameOf(value);
  return host === 'kissandra.ai' || host === 'www.kissandra.ai'
    || host === 'kissandraai.com' || host === 'www.kissandraai.com';
};

export const isProductBrowserHost = (value) => {
  const host = hostnameOf(value);
  return PRODUCT_HOSTS.has(host) || isLocalDevHost(host);
};

export const canonicalRedirectUrl = (requestUrl) => {
  const url = new URL(requestUrl);
  const host = url.hostname.toLowerCase();
  if (ALIAS_HOSTS.has(host) || host === 'www.kissandra.ai') {
    url.protocol = 'https:';
    url.hostname = 'kissandra.ai';
    url.port = '';
    return url.toString();
  }
  if (host === 'www.kissandra.red') {
    url.protocol = 'https:';
    url.hostname = 'kissandra.red';
    url.port = '';
    return url.toString();
  }
  return null;
};

export const isBlockedOnSfwHost = (r2Key) => {
  const key = String(r2Key || '').replace(/^\/+/, '');
  if (key.endsWith('.sfw.json')) return false;
  if (key.startsWith('s/modq/')) return true;
  if (NSFW_MEDIA_RE.test(key)) return true;
  return UNFILTERED_CATALOG_RE.test(key);
};

export const sfwSiblingKey = (key) => {
  const normalized = String(key || '').replace(/^\/+/, '');
  if (!normalized.endsWith('.json') || normalized.endsWith('.sfw.json')) return null;
  return normalized.replace(/\.json$/, '.sfw.json');
};

export const catalogKeyForPolicy = (key, policy) => {
  const normalized = String(key || '').replace(/^\/+/, '');
  if (policy !== 'sfw' || !isBlockedOnSfwHost(normalized)) return normalized;
  return sfwSiblingKey(normalized) || normalized;
};

const recordIsNsfw = (record) => {
  if (!record || typeof record !== 'object') return false;
  if (record.isN === 1 || record.isN === true || record.isN === '1') return true;
  if (record.is_nsfw === true || record.is_nsfw === 1 || record.is_nsfw === '1') return true;
  if (record.nsfw === true || record.nsfw === 1) return true;
  if (record.sfw_hidden === true || record.sfw_hidden === 1 || record.sfw_hidden === '1') return true;
  return false;
};

const stripNsfwMediaFields = (record) => {
  if (!record || typeof record !== 'object' || Array.isArray(record)) return record;
  const copy = { ...record };
  Object.entries(copy).forEach(([key, value]) => {
    if (typeof value === 'string' && NSFW_MEDIA_RE.test(value.split('?')[0])) delete copy[key];
  });
  return copy;
};

export const toSfwCatalog = (data) => {
  if (Array.isArray(data)) {
    return data.filter((row) => !recordIsNsfw(row)).map((row) => stripNsfwMediaFields(row));
  }
  if (!data || typeof data !== 'object') return data;
  const copy = { ...data };
  ['outfits', 'scenarios', 'characters', 'rankings', 'locations'].forEach((key) => {
    if (Array.isArray(copy[key])) copy[key] = toSfwCatalog(copy[key]);
  });
  const nestedKeys = Object.keys(copy).filter((key) => !['outfits', 'scenarios', 'characters', 'rankings', 'locations'].includes(key));
  const looksLikeMap = nestedKeys.length > 0 && nestedKeys.every((key) => copy[key] && typeof copy[key] === 'object');
  if (looksLikeMap && !Array.isArray(data)) {
    nestedKeys.forEach((key) => {
      if (recordIsNsfw(copy[key])) {
        delete copy[key];
        return;
      }
      copy[key] = stripNsfwMediaFields(copy[key]);
    });
  }
  return copy;
};

export const sfwCatalogBody = (data) => JSON.stringify(toSfwCatalog(data));

export const fundingSourceForPolicy = (policy) => (policy === 'full' ? 'crypto' : 'sfw_host');

export const userCanGenerateNsfw = async (env, accessToken) => {
  const supabaseUrl = env?.SUPABASE_URL;
  const supabaseKey = env?.SUPABASE_KEY || env?.SUPABASE_SERVICE_KEY;
  if (!supabaseUrl || !supabaseKey || !accessToken) return false;
  try {
    const response = await fetch(`${supabaseUrl}/rest/v1/rpc/user_can_generate_nsfw`, {
      method: 'POST',
      headers: {
        apikey: supabaseKey,
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
      body: '{}',
    });
    if (!response.ok) return false;
    return (await response.json()) === true;
  } catch {
    return false;
  }
};
